import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, MessageCircle } from 'lucide-react';
import apiClient from '../../utils/api';
import AppHeader from '../Header/AppHeader';
import './ChatPage.css';

// Simple Markdown renderer for chat messages
const renderMarkdown = (text) => {
  if (!text) return text;
  
  // Split by code blocks first to preserve them
  const parts = text.split(/(```[\s\S]*?```|`[^`]+`)/g);
  
  return parts.map((part, idx) => {
    // Code blocks
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3).trim();
      return <pre key={idx} className="code-block">{code}</pre>;
    }
    
    // Inline code
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={idx} className="inline-code">{part.slice(1, -1)}</code>;
    }
    
    // Process regular text with bold and italic
    let processed = part;
    
    // Bold text: **text** or __text__
    const boldRegex = /(\*\*|__)(.*?)\1/g;
    const boldParts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = boldRegex.exec(processed)) !== null) {
      if (match.index > lastIndex) {
        boldParts.push(processed.slice(lastIndex, match.index));
      }
      boldParts.push(<strong key={`bold-${idx}-${match.index}`}>{match[2]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < processed.length) {
      boldParts.push(processed.slice(lastIndex));
    }
    
    return boldParts.length > 0 ? <span key={idx}>{boldParts}</span> : part;
  });
};

function ChatPage() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadChatHistory();
    loadUsage();
    loadSubscriptionStatus();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      const data = await apiClient.getChatHistory();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const loadUsage = async () => {
    try {
      const data = await apiClient.getChatUsage();
      setUsage(data);
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage;
    setInputMessage('');
    setLoading(true);

    // Add user message immediately
    const tempUserMsg = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      // Send message with current language
      const response = await apiClient.sendChatMessage(userMessage, i18n.language);
      
      // Add assistant response
      const assistantMsg = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);
      
      // Update usage (reload to get latest coin balance)
      loadUsage();
    } catch (error) {
      console.error('Error sending message:', error);
      // Show error message
      const errorMsg = {
        role: 'assistant',
        content: t('companion.errorMessage', { error: error.message }),
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="chat-page" data-testid="chat-page">
        <div className="chat-page-container">
        <div className="chat-page-header">
          <div className="chat-page-title">
            <MessageCircle size={28} />
            <div>
              <h1>{t('companion.pageTitle')}</h1>
              <p>{t('companion.pageSubtitle')}</p>
            </div>
          </div>
          {usage && (
            <div className="chat-page-usage" data-testid="chat-page-usage">
              <span className="usage-label">💰 {t('companion.yourCoins')}</span>
              <span className="usage-count">{usage.coins_remaining}</span>
              <span className="usage-hint" style={{fontSize: '12px', opacity: 0.7}}>
                {t('companion.costPerMessage', { cost: usage.coins_per_message })}
              </span>
            </div>
          )}
        </div>

        <div className="chat-page-body">
          {messages.length === 0 ? (
            <div className="chat-page-welcome" data-testid="chat-page-welcome">
              <div className="welcome-icon">✨</div>
              <h2>{t('companion.welcomePageTitle')}</h2>
              <p>{t('companion.welcomeText')}</p>
              <div className="welcome-features">
                <div className="welcome-feature">
                  <span className="feature-icon">📖</span>
                  <span>{t('companion.features.journal')}</span>
                </div>
                <div className="welcome-feature">
                  <span className="feature-icon">🎯</span>
                  <span>{t('companion.features.frequencies')}</span>
                </div>
                <div className="welcome-feature">
                  <span className="feature-icon">💫</span>
                  <span>{t('companion.features.mindfulness')}</span>
                </div>
              </div>
              <p className="welcome-cta">{t('companion.welcomeCta')}</p>
            </div>
          ) : (
            <div className="chat-messages-container">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`chat-page-message ${msg.role}`}
                  data-testid={`chat-page-message-${msg.role}`}
                >
                  <div className="message-avatar">
                    {msg.role === 'user' ? '👤' : '✨'}
                  </div>
                  <div className="message-bubble">
                    <div className="message-content">{renderMarkdown(msg.content)}</div>
                    <div className="message-time">
                      {new Date(msg.timestamp).toLocaleString(isGerman ? 'de-DE' : 'en-US', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="chat-page-message assistant" data-testid="chat-page-loading">
                  <div className="message-avatar">✨</div>
                  <div className="message-bubble">
                    <div className="message-content">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <div className="chat-page-footer">
          <form onSubmit={handleSendMessage} className="chat-page-input-form">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={t('companion.placeholder')}
              disabled={loading}
              data-testid="chat-page-input"
            />
            <button
              type="submit"
              disabled={loading || !inputMessage.trim()}
              data-testid="chat-page-send-button"
            >
              <Send size={20} />
              <span>{t('companion.send')}</span>
            </button>
          </form>
        </div>
      </div>
      </div>
    </>
  );
}

export default ChatPage;